import React, { useState } from 'react'
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import i18nextConfig from '@/../next-i18next.config';
import Head from 'next/head'
import Header from '../../header'
import Footer from '../../footer'
import CopyPaste from '../../images/copyPaste';
import OfficeBuilding from '../../images/officeBuilding';
import MailSend from '../../images/mailSend';
import DictionaryLanguage from '../../images/dictionaryLanguage';
import DesktopCheck from '../../images/desktopCheck';
import PhoneQr from '../../images/phoneQr';
import TextFlow from '../../images/textFlow';
import CommandStreamline from '../../images/commandStreamline';
import UserSync from '../../images/userSync';
import EmailGenerator from '../../images/emailGenerator';
import CollaborationsIdea from '../../images/collaborationsIdea';
import Promo from '../../promo';
import styles from '@/styles/Features.module.css';
import Image, { ImageLoaderProps } from 'next/image';
import "swiper/css";
import { IMAGE_DELIVERY, imageLoader } from '@/base/utils/dataHelper';
import { useRouter } from 'next/router';

enum ProductUsageEnum {
  It,
  HumanResources,
  Marketing,
  ArGe,
}

const Features = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const [productUsageTab, setProductUsageTab] = useState(ProductUsageEnum.It)
  const router = useRouter();

  const isLanguage = router.locale?.toUpperCase().toString()
  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/work-management/features';

  return (
    <>
      <Head>
        <title>{t('seo_work_management_features_title')}</title>
        <meta name="description" content={t('seo_work_management_features_description')} />
        <meta name="keywords" content={locale === 'tr'
          ? "proje yönetimi, görev takibi, iş akışları, özellikler, Stoneity, work management features"
          : "project management, task tracking, workflows, features, Stoneity, work management features"} />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_work_management_features_title')} />
        <meta property="og:description" content={t('seo_work_management_features_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_work_management_features_title')} />
        <meta name="twitter:description" content={t('seo_work_management_features_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <div className={styles.mainFlex}>
        <div className={styles.pageFlex} style={{ background: '#FBFAF0' }}>
          <div className={styles.featuresBannerWrapper}>
            <div className={styles.featuresBannerTextWrapper}>
              <div className={styles.title}>{t('changeTheFlowOfYourBusinessWithStoneity')}</div>
              <div className={styles.text}>{t('changeTheFlowOfYourBusinessWithStoneityExplain')}</div>
            </div>
            <Image
              className={styles.featuresBannerImg}
              src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/f35f1ff8-aa9b-4df9-b01b-245716cea700` : `${IMAGE_DELIVERY}/16f7b953-2d9e-46de-ec25-67ce9e729900`}
              alt=""
              width={1440}
              height={355}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 1440, 355)}
            />
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.productUsageWrapper}>
            <div className={styles.title}>{t('seeHowDifferentDepartmentsUtilizeStoneity')}</div>
            <div className={styles.productUsageSwiperWrapper}>
              <Swiper
                breakpoints={{
                  0: { slidesPerView: 1 },
                }}
                slidesPerView={4}
                className={styles.productUsageMySwiper}
                modules={[Navigation]}
                navigation={{
                  prevEl: "." + styles.productUsageSlideWrapper,
                  nextEl: "." + styles.productUsageSlideWrapper2,
                }}
              >
                <SwiperSlide>
                  <div className={styles.responsiveProductUsageTab}>{t('productUsageTabIt')}</div>
                  <div className={styles.responsiveProductUsageContentIt}>
                    <Image
                      className={styles.productUsageImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/fbdac6b5-ea58-4da1-eb24-b4214b149200` : `${IMAGE_DELIVERY}/9e1afed3-c4b8-4711-3eef-f9978c019800`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                    <ul className={styles.productUsageContentList}>
                      <li>{t('productUsageTabItExplain1')}</li>
                      <li>{t('productUsageTabItExplain2')}</li>
                      <li>{t('productUsageTabItExplain3')}</li>
                    </ul>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductUsageTab}>{t('productUsageTabHumanResources')}</div>
                  <div className={styles.responsiveProductUsageContentHumanResources}>
                    <Image
                      className={styles.productUsageImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/e2e8db05-6021-4dbf-a3b2-d47eecdfd500` : `${IMAGE_DELIVERY}/8832b7a6-93f1-433e-d1e8-e6696997f600`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                    <ul className={styles.productUsageContentList}>
                      <li>{t('productUsageTabHumanResourcesExplain1')}</li>
                      <li>{t('productUsageTabHumanResourcesExplain2')}</li>
                      <li>{t('productUsageTabHumanResourcesExplain3')}</li>
                    </ul>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductUsageTab}>{t('productUsageTabMarketing')}</div>
                  <div className={styles.responsiveProductUsageContentMarketing}>
                    <Image
                      className={styles.productUsageImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/c2631d69-977c-4760-0dd8-aa6fdd48c900` : `${IMAGE_DELIVERY}/e42c0e88-259a-4de5-842c-a5aea9116c00`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                    <ul className={styles.productUsageContentList}>
                      <li>{t('productUsageTabMarketingExplain1')}</li>
                      <li>{t('productUsageTabMarketingExplain2')}</li>
                      <li>{t('productUsageTabMarketingExplain3')}</li>
                    </ul>
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductUsageTab}>{t('productUsageTabArGe')}</div>
                  <div className={styles.responsiveProductUsageContentArGe}>
                    <Image
                      className={styles.productUsageImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/20e6de66-8c57-46d2-0da8-0e3a6035dc00` : `${IMAGE_DELIVERY}/7ae94eb2-fbd9-4c3c-ee98-8b3046ed9800`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                    <ul className={styles.productUsageContentList}>
                      <li>{t('productUsageTabArGeExplain1')}</li>
                      <li>{t('productUsageTabArGeExplain2')}</li>
                      <li>{t('productUsageTabArGeExplain3')}</li>
                    </ul>
                  </div>
                </SwiperSlide>
              </Swiper>
              <div className={styles.productUsageSlideWrapper}>
                <div className={styles.productUsageCustomLeftArrow}></div>
              </div>
              <div className={styles.productUsageSlideWrapper2}>
                <div className={styles.productUsageCustomRightArrow}></div>
              </div>
            </div>
            <div className={styles.productUsageTabWrapper}>
              <div className={`${styles.productUsageTab} ${productUsageTab === ProductUsageEnum.It ? styles.productUsageTabActive : ''}`} onClick={() => { setProductUsageTab(ProductUsageEnum.It) }}>{t('productUsageTabIt')}</div>
              <div className={`${styles.productUsageTab} ${productUsageTab === ProductUsageEnum.HumanResources ? styles.productUsageTabActive : ''}`} onClick={() => { setProductUsageTab(ProductUsageEnum.HumanResources) }}>{t('productUsageTabHumanResources')}</div>
              <div className={`${styles.productUsageTab} ${productUsageTab === ProductUsageEnum.Marketing ? styles.productUsageTabActive : ''}`} onClick={() => { setProductUsageTab(ProductUsageEnum.Marketing) }}>{t('productUsageTabMarketing')}</div>
              <div className={`${styles.productUsageTab} ${productUsageTab === ProductUsageEnum.ArGe ? styles.productUsageTabActive : ''}`} onClick={() => { setProductUsageTab(ProductUsageEnum.ArGe) }}>{t('productUsageTabArGe')}</div>
            </div>
            <div className={`${styles.productUsageContentIt} ${productUsageTab === ProductUsageEnum.It ? styles.productUsageContentItActive : ''}`}>
              <Image
                className={styles.productUsageImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/fbdac6b5-ea58-4da1-eb24-b4214b149200` : `${IMAGE_DELIVERY}/9e1afed3-c4b8-4711-3eef-f9978c019800`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
              <ul className={styles.productUsageContentList}>
                <li>{t('productUsageTabItExplain1')}</li>
                <li>{t('productUsageTabItExplain2')}</li>
                <li>{t('productUsageTabItExplain3')}</li>
              </ul>
            </div>
            <div className={`${styles.productUsageContentHumanResources} ${productUsageTab === ProductUsageEnum.HumanResources ? styles.productUsageContentHumanResourcesActive : ''}`}>
              <Image
                className={styles.productUsageImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/e2e8db05-6021-4dbf-a3b2-d47eecdfd500` : `${IMAGE_DELIVERY}/8832b7a6-93f1-433e-d1e8-e6696997f600`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
              <ul className={styles.productUsageContentList}>
                <li>{t('productUsageTabHumanResourcesExplain1')}</li>
                <li>{t('productUsageTabHumanResourcesExplain2')}</li>
                <li>{t('productUsageTabHumanResourcesExplain3')}</li>
              </ul>
            </div>
            <div className={`${styles.productUsageContentMarketing} ${productUsageTab === ProductUsageEnum.Marketing ? styles.productUsageContentMarketingActive : ''}`}>
              <Image
                className={styles.productUsageImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/c2631d69-977c-4760-0dd8-aa6fdd48c900` : `${IMAGE_DELIVERY}/e42c0e88-259a-4de5-842c-a5aea9116c00`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
              <ul className={styles.productUsageContentList}>
                <li>{t('productUsageTabMarketingExplain1')}</li>
                <li>{t('productUsageTabMarketingExplain2')}</li>
                <li>{t('productUsageTabMarketingExplain3')}</li>
              </ul>
            </div>
            <div className={`${styles.productUsageContentArGe} ${productUsageTab === ProductUsageEnum.ArGe ? styles.productUsageContentArGeActive : ''}`}>
              <Image
                className={styles.productUsageImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/20e6de66-8c57-46d2-0da8-0e3a6035dc00` : `${IMAGE_DELIVERY}/7ae94eb2-fbd9-4c3c-ee98-8b3046ed9800`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
              <ul className={styles.productUsageContentList}>
                <li>{t('productUsageTabArGeExplain1')}</li>
                <li>{t('productUsageTabArGeExplain2')}</li>
                <li>{t('productUsageTabArGeExplain3')}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ background: '#F6F6F6' }}>
          <div className={styles.platformFeaturesWrapper}>
            <div className={styles.title}>{t('platformFeatures')}</div>
            <div className={styles.text}>{t('platformFeaturesExplain')}</div>
            <div className={styles.platformFeaturesCardWrapper}>
              <div className={styles.platformFeaturesCard}>
                <CopyPaste />
                <div className={styles.title}>{t('customFormFields')}</div>
                <div className={styles.text}>{t('customFormFieldsExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <OfficeBuilding />
                <div className={styles.title}>{t('flexibleWorkflows')}</div>
                <div className={styles.text}>{t('flexibleWorkflowsExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <MailSend />
                <div className={styles.title}>{t('emailAutomations')}</div>
                <div className={styles.text}>{t('emailAutomationsExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <DictionaryLanguage />
                <div className={styles.title}>{t('knowledgeBase')}</div>
                <div className={styles.text}>{t('knowledgeBaseExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <DesktopCheck />
                <div className={styles.title}>{t('approvalMechanism')}</div>
                <div className={styles.text}>{t('approvalMechanismExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <PhoneQr />
                <div className={styles.title}>{t('smartTaskAssignment')}</div>
                <div className={styles.text}>{t('smartTaskAssignmentExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <CollaborationsIdea />
                <div className={styles.title}>{t('liveSystemNotifications')}</div>
                <div className={styles.text}>{t('liveSystemNotificationsExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <TextFlow />
                <div className={styles.title}>{t('auditLog')}</div>
                <div className={styles.text}>{t('auditLogExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <CommandStreamline />
                <div className={styles.title}>{t('encryptFormField')}</div>
                <div className={styles.text}>{t('encryptFormFieldExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <UserSync />
                <div className={styles.title}>{t('onlineUsers')}</div>
                <div className={styles.text}>{t('onlineUsersExplain')}</div>
              </div>
              <div className={styles.platformFeaturesCard}>
                <EmailGenerator color={"#000"} />
                <div className={styles.title}>{t('integrateEmails')}</div>
                <div className={styles.text}>{t('integrateEmailsExplain')}</div>
              </div>
            </div>
          </div>
        </div>
        <Promo product="work-management" />
      </div>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || i18nextConfig.i18n.defaultLocale, ['common']))
  },
});

export default Features;